import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { School, Shield, Users, Zap, ArrowRight, Lightbulb, BookOpen, Briefcase, GraduationCap, Calendar, MessageCircle, Eye, ShieldCheck } from 'lucide-react';

export default function ForSchoolsPage() {
  const benefits = [
    { icon: Lightbulb, title: 'Beyond Career Counselling', desc: 'Help students move beyond career information to conversations and experiences with real professionals.' },
    { icon: Users, title: 'Powered by Your School Community', desc: 'Bring together parents, alumni and professionals approved by your school to support student exploration.' },
    { icon: Eye, title: 'Explore Before They Decide', desc: 'Help students understand careers, educational pathways and real working environments before making important choices.' },
    { icon: Shield, title: 'School-Led & Parent-Supported', desc: 'Schools manage their communities, approve mentors and coordinate experiences with parental involvement and appropriate safeguards.' },
  ];

  const steps = [
    { num: '01', title: 'Your School Joins', desc: 'Complete school onboarding and establish your school\u2019s CareersPOV community.' },
    { num: '02', title: 'Set Up Your Community', desc: 'Your coordinator manages student onboarding and approves participating mentors.' },
    { num: '03', title: 'Parents Get Involved', desc: 'Parents activate their accounts, provide required permissions and support their children\u2019s participation.' },
    { num: '04', title: 'Exploration Begins', desc: 'Students explore careers and participate in school-approved experiences with real professionals.' },
  ];

  const experiences = [
    { icon: Lightbulb, title: 'Career Exploration', desc: 'Discover careers, educational pathways and possibilities.' },
    { icon: MessageCircle, title: 'Online Career Sessions', desc: 'Connect with professionals and ask questions about their work.' },
    { icon: Briefcase, title: 'Workplace Visits', desc: 'Visit professional environments and see careers in action.' },
    { icon: Eye, title: 'Job Shadowing', desc: 'Observe professionals during their working day.' },
    { icon: GraduationCap, title: 'Internships', desc: 'Gain structured, practical exposure to professional life.' },
    { icon: BookOpen, title: 'School Workshops', desc: 'Learn directly from professionals through school-organized sessions.' },
  ];

  return (
    <div data-testid="for-schools-page">
      {/* Hero */}
      <section className="pt-12 pb-10 sm:pt-16 sm:pb-12" data-testid="schools-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fade-in-up">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#F97D6A] bg-[#FFF0ED] px-3 py-1.5 rounded-full mb-6">
            For Schools
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#1F2937] font-['Outfit'] leading-[1.1]">
            Bring the Real World of Careers<br className="hidden sm:block" /> Into Your School
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#4B5563] max-w-2xl mx-auto leading-relaxed">
            Help students discover careers, connect with real professionals and explore opportunities beyond the classroom. All within a school-led, parent-supported environment.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to="/school-registration">
              <Button className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold px-6 py-3 h-auto" data-testid="schools-register-btn">
                Register School <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="rounded-xl font-semibold px-6 py-3 h-auto border-gray-200 text-[#1F2937]" data-testid="schools-demo-btn">
                Request Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Founding Story */}
      <section className="pb-12 sm:pb-16" data-testid="schools-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-fade-in-up text-center max-w-2xl mx-auto">
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9CA3AF]">
              Built from a real question
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit'] mt-3 mb-6">
              Built From a Student's Question
            </h2>
            <p className="text-[#1F2937] text-xl sm:text-[22px] font-medium font-['Outfit'] leading-[1.5] italic">
              "Can I experience different careers before I choose my subjects?"
            </p>
            <p className="mt-4 text-base text-[#4B5563]">A simple question that inspired CareersPOV.</p>
            <Link to="/about" className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-[#F97D6A] hover:underline" data-testid="schools-read-story-link">
              Read Our Story <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Schools Choose Us */}
      <section className="py-14 bg-white" data-testid="schools-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-[#1F2937] font-['Outfit'] mb-10">
            Why Schools Choose Us
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((b, i) => (
              <div key={b.title} className={`p-6 rounded-2xl border border-gray-100 bg-[#F5F6F8] card-hover animate-fade-in-up stagger-${i + 1}`} data-testid={`benefit-card-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center mb-4 shadow-sm">
                  <b.icon className="h-5 w-5 text-[#F97D6A]" />
                </div>
                <h3 className="text-base font-bold text-[#1F2937] font-['Outfit'] mb-2">{b.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Get Started */}
      <section className="py-14" id="how-to-get-started" data-testid="schools-steps">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-[#1F2937] font-['Outfit'] mb-10">
            How to Get Started
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div key={s.num} className={`animate-fade-in-up stagger-${i + 1}`} data-testid={`step-card-${i}`}>
                <span className="text-4xl font-extrabold text-[#F97D6A]/20 font-['Outfit']">{s.num}</span>
                <h3 className="text-base font-bold text-[#1F2937] font-['Outfit'] mt-2 mb-2">{s.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Experiences */}
      <section className="py-14 bg-white" data-testid="schools-use-cases">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-[#1F2937] font-['Outfit'] mb-3">
            Career Experiences Your School Can Offer
          </h2>
          <p className="text-center text-sm text-[#6B7280] mb-8">From career curiosity to real-world learning.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {experiences.map((uc, i) => (
              <div
                key={uc.title}
                className={`bg-[#F5F6F8] rounded-2xl border border-gray-100 p-6 card-hover animate-fade-in-up stagger-${(i % 4) + 1}`}
                data-testid={`use-case-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center mb-4 shadow-sm">
                  <uc.icon className="h-5 w-5 text-[#F97D6A]" />
                </div>
                <h3 className="text-base font-bold text-[#1F2937] font-['Outfit'] mb-1.5">{uc.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Student Safety */}
      <section className="py-14" data-testid="schools-student-safety">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-12 h-12 rounded-xl bg-[#FFF0ED] flex items-center justify-center mx-auto mb-5">
            <ShieldCheck className="h-6 w-6 text-[#F97D6A]" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit'] mb-4 max-w-2xl mx-auto">
            Built Around Your School.<br className="hidden sm:block" /> Designed With Student Safety in Mind.
          </h2>
          <p className="text-base text-[#4B5563] leading-relaxed max-w-2xl mx-auto mb-3">
            CareersPOV supports school-managed career experiences with appropriate parental involvement and safeguarding arrangements.
          </p>
          <p className="text-base text-[#4B5563] leading-relaxed max-w-2xl mx-auto mb-6">
            Schools manage their communities, approve mentors and coordinate student experiences. Parents authorize participation, while mentors and host organizations follow clear professional conduct and student safety guidelines.
          </p>
          <Link to="/student-safety" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#F97D6A] hover:underline" data-testid="safety-guidelines-link">
            Read Our Student Safety Guidelines <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="pt-4 pb-16" data-testid="schools-bottom-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="rounded-2xl p-8 sm:p-10 animate-fade-in-up max-w-3xl mx-auto"
            style={{ background: 'linear-gradient(135deg, #FFF5F3 0%, #FFF0ED 50%, #FDE8E4 100%)' }}
          >
            <h3 className="text-xl sm:text-2xl font-bold text-[#1F2937] font-['Outfit'] mb-2">
              Ready to Bring CareersPOV to Your School?
            </h3>
            <p className="text-sm text-[#6B7280] mb-6 max-w-md mx-auto leading-relaxed">
              Give your students opportunities to explore careers, meet professionals and experience the world beyond the classroom.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/school-registration">
                <Button className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold px-6 py-3 h-auto" data-testid="schools-cta-register">
                  Register School <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="rounded-xl font-semibold px-6 py-3 h-auto border-gray-200 bg-white/60 text-[#1F2937]" data-testid="schools-cta-demo">
                  Request Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
